import type { Dictionary } from '@/i18n/dictionary'
import { mediusTermsHr } from '@/i18n/medius-terms'
import { mediusDeletionHr, mediusPrivacyHr } from '@/i18n/medius-policies'
import { sparsaAcceptableUseHr, sparsaTermsHr } from '@/i18n/sparsa-terms'
import { sparsaDeletionHr, sparsaPrivacyHr } from '@/i18n/sparsa-policies'

export const hr: Dictionary = {
  meta: {
    home: {
      title: 'Dalmos Systems — Poslovni softver po mjeri, CRM i integracije',
      description:
        'Dalmos Systems gradi poslovni softver po mjeri za financije, nekretnine, ugostiteljstvo i logistiku — CRM, ERP i PMS integracije, mobilne aplikacije i cloud infrastrukturu, uz dugoročno održavanje.',
      keywords: [
        'poslovni softver po mjeri',
        'CRM implementacija',
        'softver za nekretnine',
        'softver za hotele i ugostiteljstvo',
        'ERP i PMS integracije',
        'API integracije',
        'Microsoft Dynamics 365',
        'Power Platform',
        'razvoj softvera Zagreb',
      ],
    },
    privacy: {
      title: 'Pravila privatnosti — Dalmos Systems',
      description:
        'Kako Dalmos Systems obrađuje osobne podatke prikupljene putem kontaktnog obrasca, u skladu s GDPR-om.',
    },
    legal: {
      title: 'Impresum — Dalmos Systems',
      description:
        'Pravni podaci o obrtu Dalmos Systems: puni naziv, sjedište u Šibeniku, upis u Obrtni registar i kontakt.',
    },
    mediusPrivacy: {
      title: 'Pravila privatnosti za Medius — Dalmos Systems',
      description:
        'Kako Medius, usluga obrta Dalmos Systems, u ime svojih korisnika obrađuje osobne podatke iz e-pošte, chata, WhatsAppa, Messengera, Instagrama i poziva.',
    },
    mediusDeletion: {
      title: 'Brisanje podataka iz usluge Medius — Dalmos Systems',
      description:
        'Kako zatražiti brisanje osobnih podataka iz usluge Medius, uključujući poruke primljene putem Facebook Messengera, Instagrama i WhatsAppa.',
    },
    mediusTerms: {
      title: 'Uvjeti korištenja usluge Medius — Dalmos Systems',
      description:
        'Uvjeti pod kojima obrt Dalmos Systems poslovnim korisnicima pruža Medius, radni prostor za e-poštu, chat, poruke i pozive.',
    },
    sparsaPrivacy: {
      title: 'Pravila privatnosti za Sparsu — Dalmos Systems',
      description:
        'Kako Sparsa by Dalmos obrađuje osobne podatke primatelja newslettera i korisnika aplikacije te otvaranja i klikove mjeri samo uz privolu.',
    },
    sparsaDeletion: {
      title: 'Brisanje podataka iz Sparse — Dalmos Systems',
      description:
        'Kako prestati primati newslettere poslane putem Sparse by Dalmos i kako zatražiti brisanje svojih osobnih podataka.',
    },
    sparsaTerms: {
      title: 'Uvjeti korištenja usluge Sparsa — Dalmos Systems',
      description:
        'Uvjeti pod kojima obrt Dalmos Systems tvrtkama i drugim organizacijama pruža Sparsu by Dalmos, uslugu za slanje newslettera.',
    },
    sparsaAcceptableUse: {
      title: 'Prihvatljivo korištenje usluge Sparsa — Dalmos Systems',
      description:
        'Pravila slanja putem Sparse by Dalmos: privola za svakog primatelja, bez kupljenih ili prikupljenih popisa, izjava pri uvozu i prijava zlouporabe.',
    },
    notFound: {
      title: 'Stranica nije pronađena — Dalmos Systems',
      description: 'Traženu stranicu nismo našli.',
    },
  },
  nav: {
    home: 'Početna',
    services: 'Usluge',
    about: 'O nama',
    contact: 'Kontakt',
    cta: 'Zatražite ponudu',
    openMenu: 'Otvorite navigaciju',
    closeMenu: 'Zatvorite navigaciju',
    languageLabel: 'Odaberite jezik',
    currentLanguage: 'Trenutni jezik',
  },
  a11y: {
    skipToContent: 'Preskoči na sadržaj',
    decorative: 'Dekorativni element',
  },
  hero: {
    eyebrow: 'Softverska rješenja po mjeri za poslovanje',
    headlineBefore: 'Sustavi koji rade za vaš posao,',
    headlineAccent: 'ne obrnuto.',
    subtitle:
      'Tehnički partner za poslovni softver po mjeri — rješenja za financije, nekretnine, ugostiteljstvo i logistiku, izgrađena na iskustvu s velikih CRM projekata i Microsoftovim ekosustavom kao pouzdanim temeljem, ne jedinim fokusom.',
    ctaPrimary: 'Zatražite ponudu',
    ctaSecondary: 'Pogledajte usluge',
    chips: [
      'CRM i ERP integracije',
      'Ugostiteljstvo i PMS',
      'Financijska rješenja',
      'Nekretnine',
      'Mobilne aplikacije',
      'Warehouse i facility',
    ],
    scrollHint: 'Listajte',
  },
  stats: {
    title: 'Na čemu gradimo',
    items: [
      { value: '20+', label: 'Područja usluge pod jednim krovom' },
      { value: '6', label: 'Industrija s gotovim obrascima rješenja' },
      { value: '1', label: 'Osoba koja vodi projekt i piše kod' },
      { value: '100%', label: 'Projekata ostaje uz definiranu podršku' },
    ],
  },
  why: {
    title: 'Zašto Dalmos Systems',
    cards: [
      {
        title: 'Direktan pristup',
        body: 'Bez slojeva agencije — razgovarate izravno s osobom koja projekt i gradi.',
      },
      {
        title: 'Rješenja prilagođena industriji',
        body: 'Od financija i nekretnina do ugostiteljstva i logistike — svaki sustav gradi se oko vaših stvarnih procesa.',
      },
      {
        title: 'Partner, ne izvođač',
        body: 'Ostajemo uz sustav nakon isporuke — održavanje, nadogradnje i podrška dio su dogovora.',
      },
    ],
  },
  services: {
    title: 'Širok raspon rješenja, jedan izvor',
    lede: 'Od financija i nekretnina do ugostiteljstva i infrastrukture u oblaku — svaka usluga dolazi s implementacijom i dugoročnim održavanjem, bez prevelikog fokusa na jedno usko područje.',
    items: {
      crm: {
        title: 'CRM i prilagodbe',
        body: 'Uvođenje i prilagodba CRM sustava vašim procesima, timovima i klijentima.',
      },
      erp: {
        title: 'ERP sustavi i integracije',
        body: 'Povezivanje ERP-a s prodajom, skladištem i financijama te prilagodbe postojećih modula.',
      },
      processes: {
        title: 'Procesi i optimizacija poslovanja',
        body: 'Automatizacija internih tokova rada i aplikacije koje ubrzavaju odlučivanje i smanjuju ručni rad.',
      },
      finance: {
        title: 'Financijska rješenja',
        body: 'Sustavi za praćenje troškova, naplate i financijsko izvještavanje.',
      },
      realEstate: {
        title: 'Nekretnine i upravljanje imovinom',
        body: 'Vođenje portfelja nekretnina, ugovora o najmu i procjena.',
      },
      mobile: {
        title: 'Mobilne aplikacije',
        body: 'Mobilna proširenja poslovnih sustava za teren, skladište ili prodaju.',
      },
      hospitality: {
        title: 'Rješenja za ugostiteljstvo',
        body: 'Sustavi za hotele, restorane i smještajne objekte — PMS integracije, rezervacije, podaci o gostima i operativa.',
      },
      web: {
        title: 'Web rješenja i portali',
        body: 'Web stranice usklađene s vašim poslovnim sustavom.',
      },
      reservations: {
        title: 'Rezervacijski sustavi',
        body: 'Online rezervacije u stvarnom vremenu, povezane s PMS-om i naplatom.',
      },
      analytics: {
        title: 'Statistike i izvještaji',
        body: 'Dashboardi za odluke temeljene na stvarnim podacima.',
      },
      loyalty: {
        title: 'Loyalty sustavi',
        body: 'Programi vjernosti s bodovima, razinama i nagradama, članskim karticama te mobilnom aplikacijom ili karticom u digitalnom novčaniku. Ponude i kampanje prate povijest kupnje svakog člana, a sve ostaje povezano s vašim CRM-om.',
      },
      marketing: {
        title: 'Marketing i newsletteri',
        body: 'Newsletteri i kampanje prema kontaktima iz vašeg CRM-a, s ugrađenim privolama i odjavama.',
      },
      communication: {
        title: 'Komunikacijska rješenja',
        body: 'Rješenja po mjeri za komunikaciju vašeg tima s klijentima, povezana s vašim CRM-om ili samostalna.',
      },
      warehouse: {
        title: 'Upravljanje skladištem',
        body: 'Praćenje zaliha i kretanja robe.',
      },
      facility: {
        title: 'Upravljanje objektima',
        body: 'Upravljanje objektima i održavanjem.',
      },
      api: {
        title: 'API integracije i middleware',
        body: 'Povezivanje CRM, ERP, PMS i drugih sustava koji ne razgovaraju.',
      },
      cloud: {
        title: 'Cloud infrastruktura i DevOps',
        body: 'Azure okruženja, CI/CD i automatizacija.',
      },
      microsoft: {
        title: 'Microsoftov ekosustav',
        body: 'Dynamics 365, Power Platform i Azure, kad su prava opcija za posao.',
      },
      security: {
        title: 'IT sigurnost i usklađenost',
        body: 'Zaštita podataka i usklađenost s GDPR-om.',
      },
      custom: {
        title: 'Custom softverska rješenja',
        body: 'Rješenja po mjeri za specifične potrebe.',
      },
      training: {
        title: 'Edukacija i onboarding',
        body: 'Obuka korisnika i interna dokumentacija.',
      },
      support: {
        title: 'Podrška i SLA ugovori',
        body: 'Definirani rokovi odziva i podrške.',
      },
    },
  },
  integrations: {
    title: 'Sustavi koji konačno razgovaraju',
    lede: 'CRM, ERP i PMS rijetko su povezani sami od sebe. Spajamo ih tako da se podaci unose jednom, a vide svugdje — bez ručnog prepisivanja i Excel tablica između.',
    hubLabel: 'Jedan izvor podataka',
    systems: [
      {
        code: 'CRM',
        title: 'Kupci i prodaja',
        body: 'Klijenti, prilike i komunikacija na jednom mjestu, sinkronizirani s ponudama, ugovorima i naplatom.',
      },
      {
        code: 'ERP',
        title: 'Financije i operativa',
        body: 'Fakturiranje, nabava i zalihe povezani s prodajom i skladištem, bez dvostrukog unosa.',
      },
      {
        code: 'PMS',
        title: 'Ugostiteljstvo',
        body: 'Property management sustavi povezani s rezervacijama, CRM-om i računovodstvom.',
      },
      {
        code: 'API',
        title: 'Sve ostalo',
        body: 'Računovodstvo, webshop, POS, channel manageri, BI alati i interne aplikacije.',
      },
    ],
    capabilitiesTitle: 'Što dobivate',
    capabilities: [
      'Dvosmjerna sinkronizacija podataka u stvarnom vremenu',
      'Migracije podataka bez zastoja u radu',
      'Middleware i API-ji po mjeri',
      'Nadzor, zapisi i upozorenja kad nešto zapne',
    ],
  },
  about: {
    title: 'O nama',
    statementBefore: 'Nova tvrtka.',
    statementAccent: 'Iskustvo koje ne počinje od nule.',
    lede: 'Dalmos Systems osnovan je kao tehnički partner za poslovni softver po mjeri — financije, nekretnine, ugostiteljstvo, logistika i više. Pristup je izravan nastavak rada na velikim projektima migracije i integracije CRM sustava u sektorima nekretnina i hotelijerstva, bez sloja velike organizacije između.',
    processTitle: 'Kako radimo',
    steps: [
      { index: '01', title: 'Otkrivanje', body: 'Razumijevanje procesa prije prvog reda koda.' },
      {
        index: '02',
        title: 'Dizajn rješenja',
        body: 'Arhitektura prilagođena vašim sustavima, ne obrnuto.',
      },
      {
        index: '03',
        title: 'Implementacija',
        body: 'Iterativna isporuka uz redovite provjere s vama.',
      },
      { index: '04', title: 'Podrška', body: 'Ostajemo uz sustav i nakon lansiranja.' },
    ],
    valuesTitle: 'Što nas vodi',
    values: [
      {
        title: 'Direktan pristup',
        body: 'S klijentom razgovara osoba koja i vodi projekt, bez slojeva komunikacije.',
      },
      {
        title: 'Transparentnost',
        body: 'Jasan opseg, jasna cijena i jasne granice projekta prije prvog retka koda.',
      },
      {
        title: 'Dugoročnost',
        body: 'Gradimo sustave koje ćemo i sami održavati, ne projekte koje ostavljamo iza sebe.',
      },
    ],
  },
  contact: {
    title: 'Razgovarajmo o vašem sustavu',
    lede: 'Javite se s opisom projekta — odgovaramo s prijedlogom sljedećeg koraka.',
    responseNote: 'Odgovaramo u roku od {days} radna dana.',
    emailLabel: 'E-mail',
    phoneLabel: 'Telefon',
    form: {
      name: 'Ime i prezime',
      namePlaceholder: 'Ana Anić',
      email: 'E-mail',
      emailPlaceholder: 'ana@firma.hr',
      company: 'Tvrtka',
      companyPlaceholder: 'Naziv tvrtke (nije obavezno)',
      message: 'Poruka',
      messagePlaceholder: 'Opišite projekt, sustave koje koristite i željeni rok.',
      consent: 'Pristajem da se moji podaci koriste za odgovor na ovaj upit.',
      submit: 'Pošaljite upit',
      submitting: 'Šaljemo…',
      successTitle: 'Upit je poslan.',
      successBody: 'Javljamo se na navedenu e-mail adresu.',
      errorTitle: 'Slanje nije uspjelo.',
      errorBody: 'Pokušajte ponovno ili nam pišite izravno na e-mail.',
      fallbackHint: 'Otvorite e-mail klijent',
      errors: {
        nameRequired: 'Unesite ime i prezime.',
        emailRequired: 'Unesite e-mail adresu.',
        emailInvalid: 'E-mail adresa nije valjana.',
        messageRequired: 'Unesite poruku.',
        messageTooShort: 'Poruka mora imati najmanje 20 znakova.',
        consentRequired: 'Potrebna je privola za obradu podataka.',
      },
    },
  },
  footer: {
    tagline: 'Poslovni softver po mjeri za financije, nekretnine, ugostiteljstvo i logistiku.',
    navTitle: 'Navigacija',
    legalTitle: 'Pravno',
    privacy: 'Pravila privatnosti',
    legalNotice: 'Impresum',
    rights: 'Sva prava pridržana.',
    builtIn: 'Ulica Sedmog kontinenta 1, 22000 Šibenik, Hrvatska',
  },
  privacy: {
    title: 'Pravila privatnosti',
    lastUpdatedLabel: 'Zadnja izmjena',
    lastUpdated: '2026-10-31',
    intro:
      'Ova pravila opisuju kako Dalmos Systems obrađuje osobne podatke koje nam pošaljete putem ove web stranice.',
    sections: [
      {
        heading: 'Voditelj obrade',
        paragraphs: [
          'Voditelj obrade je {controller}. Za sva pitanja o obradi podataka pišite nam na {email}.',
        ],
      },
      {
        heading: 'Koje podatke prikupljamo',
        paragraphs: [
          'Putem kontaktnog obrasca prikupljamo ime i prezime, adresu e-pošte, naziv tvrtke ako ga unesete i sadržaj vaše poruke.',
          'Ne koristimo kolačiće za oglašavanje ni alate za praćenje posjetitelja na više web stranica.',
          'Posjete brojimo alatom Cloudflare Web Analytics, Cloudflareovom skriptom bez kolačića (static.cloudflareinsights.com). Kada se stranica učita, skripta Cloudflareu šalje anonimne podatke o pregledu i brzini stranice: adresu stranice, stranicu s koje ste došli te vremena učitavanja i slična mjerenja rada stranice; Cloudflare iz samog zahtjeva dodaje preglednik, operacijski sustav, vrstu uređaja i državu. Skripta ne postavlja kolačiće, ne koristi drugu pohranu na vašem uređaju i ne stvara identifikator posjetitelja. Vaša IP adresa dolazi do Cloudflarea sa zahtjevom, kao kod svake web stranice; Cloudflare je ne koristi da bi vas prepoznao i za to je ne pohranjuje. Mi vidimo samo ukupne brojke, poput pregleda stranica, web-mjesta s kojih posjetitelji dolaze, država i vrsta uređaja, i koristimo ih kako bismo razumjeli kako se web stranica koristi i održali je brzom. Pravna osnova je naš legitimni interes za rad i poboljšanje web stranice (članak 6. stavak 1. točka (f) GDPR-a). Cloudflare te podatke za nas obrađuje kao izvršitelj obrade, prema svojem dodatku o obradi podataka sa standardnim ugovornim klauzulama EU-a za prijenose izvan EU-a. Potpune podatke čuva 7 dana, a zatim uzorak od otprilike desetine njih, iz kojeg vidimo statistiku za posljednjih šest mjeseci. Skriptu možete blokirati postavkama preglednika ili alatom za blokiranje sadržaja, a web stranica i bez nje radi jednako. Isto vrijedi za web stranicu Medius, medius.dalmossystems.com, koja upućuje na ova pravila.',
        ],
      },
      {
        heading: 'Svrha i pravna osnova',
        paragraphs: [
          'Podatke koristimo isključivo kako bismo odgovorili na vaš upit i pripremili ponudu. Pravna osnova je vaša privola te naš legitimni interes za komunikaciju s potencijalnim klijentima.',
        ],
      },
      {
        heading: 'Rok čuvanja',
        paragraphs: [
          'Upite čuvamo najdulje dvadeset četiri mjeseca od zadnje komunikacije, nakon čega ih brišemo, osim ako je dulje čuvanje potrebno zbog ugovorne ili zakonske obveze.',
        ],
      },
      {
        heading: 'Vaša prava',
        paragraphs: [
          'Imate pravo na pristup podacima, ispravak, brisanje, ograničenje obrade, prenosivost podataka i pravo na povlačenje privole u svakom trenutku.',
          'Pritužbu možete podnijeti Agenciji za zaštitu osobnih podataka (AZOP) kao nadzornom tijelu u Republici Hrvatskoj.',
        ],
      },
      {
        heading: 'Sigurnost i prijenos podataka',
        paragraphs: [
          'Promet između vašeg preglednika i naših poslužitelja zaštićen je enkripcijom. Podatke ne prodajemo i ne dijelimo s trećim stranama osim pružateljima usluga koji nam pomažu u radu stranice i komunikaciji, pod ugovorom o obradi podataka.',
        ],
      },
    ],
    backHome: 'Natrag na početnu',
  },
  legal: {
    title: 'Impresum',
    intro: 'Podaci o pružatelju usluga u skladu sa Zakonom o elektroničkoj trgovini.',
    nameLabel: 'Naziv obrta',
    shortNameLabel: 'Kratki naziv',
    seatLabel: 'Sjedište',
    country: 'Hrvatska',
    registerLabel: 'Upisnik',
    registerValue: 'Obrtni registar, Šibensko-kninska županija',
    mboLabel: 'Matični broj obrta (MBO)',
    registeredOnLabel: 'Datum upisa',
    activityLabel: 'Pretežita djelatnost',
    activityValue:
      'Izrada, razvoj i održavanje računalnih programa i aplikacija po narudžbi (NKD 2025: 62.10.9)',
    contactLabel: 'Kontakt',
    backHome: 'Natrag na početnu',
  },
  mediusPrivacy: mediusPrivacyHr,
  mediusDeletion: mediusDeletionHr,
  mediusTerms: mediusTermsHr,
  mediusLinks: {
    mediusPrivacy: 'Pravila privatnosti za Medius',
    mediusDeletion: 'Kako zatražiti brisanje podataka',
    mediusTerms: 'Uvjeti korištenja usluge Medius',
  },
  sparsaPrivacy: sparsaPrivacyHr,
  sparsaDeletion: sparsaDeletionHr,
  sparsaTerms: sparsaTermsHr,
  sparsaAcceptableUse: sparsaAcceptableUseHr,
  sparsaLinks: {
    sparsaPrivacy: 'Pravila privatnosti za Sparsu',
    sparsaDeletion: 'Kako prestati primati newslettere i izbrisati podatke',
    sparsaTerms: 'Uvjeti korištenja usluge Sparsa',
    sparsaAcceptableUse: 'Pravila prihvatljivog korištenja',
  },
  notFound: {
    code: '404',
    title: 'Stranica nije pronađena',
    body: 'Adresa koju ste otvorili ne postoji ili je premještena.',
    cta: 'Natrag na početnu',
  },
}
